/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Form as FormikForm,
    Masks,
    DateTimePickerField,
    DatePickerField,
    AutocompleteField,
    InputField,
    TextareaField,
} from "components/common_components/Formik";
import * as Yup from "yup";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import FormGroup from "@material-ui/core/FormGroup";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import useFilesUploader from "components/common_components/hooks/useFilesUploader/useFilesUploader";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import FileExplorer from "components/Modules/Module/Attachments/FileExplorer";
import { ConfirmDialog } from "components/common_components/Dialogs";
import { useDispatch } from "react-redux";
import { notifyError, notifySuccess } from "redux/actions/Notifications";
import AppContext from "AppContext";
import { hasElements } from "utils/arrays";
import { sanitizeFileName } from "utils/files";
import { getDownloadURL } from "firebase/storage";

const useStyles = makeStyles((theme) => ({
    formControl: {
        margin: "unset",
        paddingTop: theme.spacing(3),
    },
    buttons: {
        marginTop: theme.spacing(2),
    },
    button: {
        margin: theme.spacing(0, 1, 0, 0),
    },
}));

function Form({ visitor, resources, mode, onEdit, ...rest }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const readOnly = mode === "detail";
    const { countries } = resources;
    const [attachments, setAttachments] = useState([]);
    const [showDragzone, setShowDragzone] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const STORAGE_DIR = `management/visitors/${visitor.id}`;
    const [currentDirectory, setCurrentDirectory] = useState(STORAGE_DIR);
    const [selectedFile, setSelectedFile] = useState(undefined);
    const [confirmDialogProps, setConfirmDialogProps] = useState({
        open: false,
        title: "",
        content: "",
        primaryButtonText: "",
        onPrimaryClick: undefined,
        secondaryButtonText: "Cancel",
        onSecondaryClick: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
        onClose: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
    });

    const {
        api: {
            General: { Storage },
        },
    } = useContext(AppContext);

    const [filesToUpload, fileNames, , reset, uploader] = useFilesUploader({
        requestOrigin: false,
    });

    const validationSchema = Yup.object({
        firstName: Yup.string().required("Required field"),
        lastName: Yup.string().required("Required field"),
        homePhone: Yup.string()
            .test("Valid home phone", "Incorrect format. Example 123-456-7890", (value) =>
                value ? /^\d\d\d-\d\d\d-\d\d\d\d/.test(value) : true
            )
            .required("Required field"),
        visitDate: Yup.date().required("Required field"),
        dob: Yup.date().required("Required field"),
        ssn: Yup.string()
            .test("Valid SSN", "Incorrect format. Example 123-45-6789", (value) => (value ? /^\d\d\d-\d\d-\d\d\d\d/.test(value) : true))
            .notRequired(),
        workPhone: Yup.string()
            .test("Valid work phone", "Incorrect format. Example 123-45-6789", (value) =>
                value ? /^\d\d\d-\d\d\d-\d\d\d\d/.test(value) : true
            )
            .notRequired(),
    });

    useEffect(() => {
        if (visitor && mode !== "add" && currentDirectory) {
            getAttachmentsAsync();
        }
    }, []);

    const getAttachmentsAsync = async () => {
        try {
            const contents = await Storage.listDirectoryContents(currentDirectory);
            if (hasElements(contents?.files)) {
                setAttachments(contents.files.map((file, index) => ({ id: index, name: file.name, url: file.url, dateString: "" })));
            } else {
                setAttachments([]);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const onDeleteHandler = (file) => {
        setConfirmDialogProps((prev) => ({
            ...prev,
            open: true,
            title: "Delete attachment",
            content: "Are you sure you want to delete this attachment?",
            primaryButtonText: "Yes, delete",
            onPrimaryClick: async () => {
                try {
                    await Storage.deleteAsync({
                        path: `${currentDirectory}/${file.name}`,
                    });
                    setSelectedFile(undefined);
                    await getAttachmentsAsync();
                    dispatch(notifySuccess("Removed successfully!"));
                } catch (error) {
                    dispatch(notifyError(error.message));
                } finally {
                    prev.onClose();
                }
            },
        }));
    };

    async function onSubmitAttachmentsHandler() {
        const requests = [];

        filesToUpload.forEach((file) => {
            const request = new Promise(async (resolve, reject) => {
                const customName = fileNames[file.name] || file.name;
                try {
                    const fileExtension = `.${file.name.split(".").pop()}`;
                    const fileName = sanitizeFileName(customName.replace(fileExtension, ""));
                    const snap = await Storage.uploadAsync({
                        path: `${currentDirectory}/${fileName}${fileExtension}`,
                        file,
                    });
                    const url = await getDownloadURL(snap.ref);

                    if (url) {
                        resolve(url);
                    } else {
                        reject({ httpError: undefined, data: file, message: `Error uploading the file ${customName}` });
                    }
                } catch (error) {
                    reject({ httpError: error, data: file, message: `Error uploading the file ${customName}` });
                }
            });

            requests.push(request);
        });

        if (requests.length > 0) {
            setIsUploading(true);
            Promise.all(requests)
                .then(async () => {
                    dispatch(notifySuccess(`Uploaded successfully!`));
                    cancelAttachFile();
                    await getAttachmentsAsync();
                })
                .catch((error) => {
                    dispatch(notifyError(error.message));
                })
                .finally(() => {
                    setIsUploading(false);
                });
        }
    }

    function cancelAttachFile() {
        reset();
        setSelectedFile(undefined);
        setShowDragzone(false);
    }

    function initAttachFile() {
        reset();
        setSelectedFile(undefined);
        setShowDragzone(true);
    }

    return (
        <>
            <div className="layout">
                <FormikForm
                    validateOnChange={false}
                    initialValues={visitor}
                    validationSchema={validationSchema}
                    showButtons={!readOnly}
                    {...rest}
                >
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={3}>
                            <FormControl component="fieldset" className={classes.formControl}>
                                <FormLabel component="legend">General Information</FormLabel>
                                <FormGroup row>
                                    <InputField disabled={readOnly} fieldName="ssn" label="SSN" lowercase mask={Masks.ssn} light compact />
                                    <InputField disabled={readOnly} fieldName="firstName" label="First Name *" light compact />
                                    <InputField disabled={readOnly} fieldName="middleName" label="Middle Name" light compact />
                                    <InputField disabled={readOnly} fieldName="lastName" label="Last Name *" light compact />
                                    <DatePickerField disabled={readOnly} fieldName="dob" label="Date of Birth *" />
                                </FormGroup>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl component="fieldset" className={classes.formControl}>
                                <FormLabel component="legend">Location</FormLabel>
                                <FormGroup row>
                                    <AutocompleteField
                                        disabled={readOnly}
                                        fieldName="country"
                                        label="Country"
                                        options={countries}
                                        valueKey="code"
                                        labelKey="name"
                                    />
                                    <InputField disabled={readOnly} fieldName="state" label="State" light compact />
                                    <InputField disabled={readOnly} fieldName="city" label="City" light compact />
                                    <InputField disabled={readOnly} fieldName="zipCode" label="Zip Code" light compact />
                                    <InputField disabled={readOnly} fieldName="address1" label="Address 1" light compact />
                                    <InputField disabled={readOnly} fieldName="address2" label="Address 2" light compact />
                                </FormGroup>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl component="fieldset" className={classes.formControl}>
                                <FormLabel component="legend">Contact</FormLabel>
                                <FormGroup row>
                                    <InputField
                                        disabled={readOnly}
                                        fieldName="homePhone"
                                        label="Home Phone *"
                                        mask={Masks.phoneNumber}
                                        light
                                        compact
                                    />
                                    <InputField
                                        disabled={readOnly}
                                        fieldName="workPhone"
                                        label="Work Phone"
                                        mask={Masks.phoneNumber}
                                        light
                                        compact
                                    />
                                </FormGroup>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl component="fieldset" className={classes.formControl}>
                                <FormLabel component="legend">Visit</FormLabel>
                                <FormGroup row>
                                    <DateTimePickerField disabled={readOnly} fieldName="visitDate" label="Visit Date *" />
                                    <TextareaField
                                        disabled={readOnly}
                                        fieldName="notes"
                                        label="Notes"
                                        fullWidth
                                        maxLength={500}
                                        rows={10}
                                    />
                                </FormGroup>
                            </FormControl>
                        </Grid>
                    </Grid>
                    {readOnly && (
                        <Grid container spacing={1} className={classes.buttons}>
                            <Grid item xs={12} sm={6}>
                                <div className="button-group">
                                    <Button primary outline onClick={rest.onCancel}>
                                        <Icon name="arrow" size="xs" color="primary" />
                                        <span>Back</span>
                                    </Button>
                                    <Button primary onClick={onEdit}>
                                        <Icon name="pencil" size="sm" color="white" />
                                        <span>Edit</span>
                                    </Button>
                                </div>
                            </Grid>
                        </Grid>
                    )}
                </FormikForm>
            </div>
            <div className="layout">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        {showDragzone && (
                            <Grid container justifyContent="flex-end" style={{ paddingTop: 20 }}>
                                <div className="button-group">
                                    <Button secondary small outline onClick={cancelAttachFile}>
                                        <Icon name="cancel" size="xs" color="secondary" />
                                        <span>Cancel</span>
                                    </Button>
                                    {hasElements(filesToUpload) && (
                                        <Button secondary small loading={isUploading} onClick={onSubmitAttachmentsHandler}>
                                            <Icon name="upload" size="sm" color="white" />
                                            <span>Upload</span>
                                        </Button>
                                    )}
                                </div>
                            </Grid>
                        )}
                    </Grid>

                    {showDragzone && (
                        <Grid item xs={12}>
                            {uploader()}
                        </Grid>
                    )}
                    {mode !== "add" && (
                        <Grid item xs={12} style={{ paddingTop: 20 }}>
                            <Grid container direction="row" justifyContent="space-between">
                                <Grid item xs={!selectedFile ? 5 : 4}>
                                    <FileExplorer
                                        rootPath={STORAGE_DIR}
                                        onDirectoryChange={setCurrentDirectory}
                                        rootTitle={""}
                                        onFileSelected={setSelectedFile}
                                        selectedFile={selectedFile}
                                        onDeleteFile={onDeleteHandler}
                                        attachmentRecords={attachments}
                                        allowCreateFolder={false}
                                        allowMoveFiles={false}
                                        initAttachFiles={initAttachFile}
                                        showToolbar={true}
                                    />
                                </Grid>
                                {selectedFile && (
                                    <Grid item xs={8}>
                                        <iframe
                                            src={selectedFile ? selectedFile.url : undefined}
                                            frameBorder="0"
                                            title="Viewer"
                                            width="100%"
                                            height="900px"
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    )}
                </Grid>
                <ConfirmDialog {...confirmDialogProps} />
            </div>
        </>
    );
}

Form.propTypes = {
    visitor: PropTypes.shape({
        visitDate: PropTypes.instanceOf(Date),
        ssn: PropTypes.string,
        firstName: PropTypes.string,
        middleName: PropTypes.string,
        lastName: PropTypes.string,
        address1: PropTypes.string,
        address2: PropTypes.string,
        country: PropTypes.shape({
            code: PropTypes.string,
            name: PropTypes.string,
        }),
        city: PropTypes.string,
        state: PropTypes.string,
        zipCode: PropTypes.string,
        dob: PropTypes.instanceOf(Date),
        homePhone: PropTypes.string,
        workPhone: PropTypes.string,
        notes: PropTypes.string,
    }),
    resources: PropTypes.shape({
        countries: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                name: PropTypes.string,
            })
        ),
    }),
    mode: PropTypes.oneOf(["add", "detail", "update"]).isRequired,
    onEdit: PropTypes.func,
};

Form.defaultProps = {
    visitor: {
        visitDate: new Date(),
        ssn: "",
        firstName: "",
        middleName: "",
        lastName: "",
        address1: "",
        address2: "",
        country: null,
        city: "",
        state: "",
        zipCode: "",
        dob: new Date(),
        homePhone: "",
        workPhone: "",
        notes: "",
    },
    resources: {
        countries: [],
    },
    onEdit: () => {},
};

export default Form;
